// ============================================================================
// AI COLOR MAGIC MODAL RENDERER
// ============================================================================
// Shared modal component for color exploration across all pages
// Provides consistent VowAI signature styling and AI Color Magic integration
// Used by: DreamWeddingGenerator, MoodboardMatcher, ThemesDashboard
// ============================================================================

import React from 'react';
import { Link } from 'react-router-dom';
import { apiService } from '../services/apiService';

/**
 * Renders the Ritual Details Modal
 * 
 * @param {Object} ritualModal - Ritual modal state { show, ritual }
 * @param {Function} setRitualModal - State setter for ritual modal
 * @returns {JSX.Element|null} Modal JSX or null if not shown
 */
export const renderRitualModal = (ritualModal, setRitualModal, timeTracker = null, themeId = null, savedRitualIds = new Set(), onSaveRitual = null, themeName = null) => {
  if (!ritualModal.show || !ritualModal.ritual) return null;

  const { ritual } = ritualModal;
  const closeModal = () => setRitualModal({ show: false, ritual: null });

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-in fade-in duration-300" onClick={closeModal}>
      <div className="relative bg-gradient-to-br from-white via-purple-50/30 to-pink-50/30 backdrop-blur-xl rounded-3xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto border-2 border-purple-200/50 transform animate-in zoom-in slide-in-from-bottom-4 duration-500" onClick={(e) => e.stopPropagation()}>
        {/* Animated gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-r from-purple-400/5 to-pink-400/5 opacity-0 hover:opacity-100 transition-opacity duration-500 rounded-3xl pointer-events-none"></div>
        
        <div className="relative p-8">
          {/* Close Button */}
          <button 
            onClick={closeModal} 
            className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center text-gray-400 hover:text-purple-600 hover:bg-purple-100 rounded-full text-2xl transition-all duration-300 hover:rotate-90"
          >
            ×
          </button>
          
          {/* Ritual Name with gradient */}
          <h2 className="text-3xl font-bold bg-gradient-to-r from-purple-600 via-pink-600 to-purple-600 bg-clip-text text-transparent mb-6 pr-8">
            {ritual.name}
          </h2>
          
          {/* Badges */}
          <div className="flex flex-wrap gap-3 mb-6">
            <span className="px-4 py-2 bg-gradient-to-r from-purple-100 to-pink-100 text-purple-700 rounded-full text-sm font-semibold shadow-sm">
              {ritual.culture_display_name}
            </span>
            {ritual.similarity && (
              <span className="flex items-center justify-center px-4 py-2 bg-gradient-to-r from-green-100 to-emerald-100 text-green-700 rounded-full text-sm font-semibold border border-green-200 shadow-sm">
                {Math.round(ritual.similarity * 100)}% match
              </span>
            )}
            {ritual.duration_minutes && (
              <span className="px-4 py-2 bg-gradient-to-r from-gray-100 to-gray-200 text-gray-700 rounded-full text-sm font-semibold shadow-sm">
                {ritual.duration_minutes} min
              </span>
            )}
          </div>

          {/* Description */}
          {ritual.description && (
            <div className="mb-6 p-4 bg-white/50 rounded-xl border border-purple-100">
              <h3 className="text-lg font-semibold text-purple-800 mb-2 flex items-center gap-2">
                <span className="text-xl">📖</span> Description
              </h3>
              <p className="text-gray-700 leading-relaxed">{ritual.description}</p>
            </div>
          )}

          {/* Meaning */}
          <div className="mb-6 p-4 bg-white/50 rounded-xl border border-purple-100">
            <h3 className="text-lg font-semibold text-purple-800 mb-2 flex items-center gap-2">
              <span className="text-xl">💫</span> Meaning
            </h3>
            <p className="text-gray-700 leading-relaxed">{ritual.meaning}</p>
          </div>

          {/* Symbolism */}
          {ritual.symbolism && (
            <div className="mb-6 p-4 bg-white/50 rounded-xl border border-purple-100">
              <h3 className="text-lg font-semibold text-purple-800 mb-2 flex items-center gap-2">
                <span className="text-xl">✨</span> Symbolism
              </h3>
              <p className="text-gray-700 leading-relaxed">{ritual.symbolism}</p>
            </div>
          )}

          {/* Themes */}
          {ritual.themes && ritual.themes.length > 0 && (
            <div className="mb-6 p-4 bg-white/50 rounded-xl border border-purple-100">
              <h3 className="text-lg font-semibold text-purple-800 mb-3 flex items-center gap-2">
                <span className="text-xl">🎨</span> Themes
              </h3>
              <div className="flex flex-wrap gap-2">
                {ritual.themes.map((theme, i) => (
                  <span key={i} className="px-3 py-1 bg-gradient-to-r from-purple-100 to-pink-100 text-purple-700 rounded-lg text-sm font-medium shadow-sm">
                    {theme}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Add to Wedding Plan Button */}
          {onSaveRitual && (
            <div className="text-center mt-8 mb-4">
              <button
                onClick={() => onSaveRitual(ritual)}
                disabled={savedRitualIds.has(ritual.id)}
                className={`inline-flex items-center gap-2 px-8 py-3 rounded-2xl font-bold text-lg transform hover:scale-105 transition-all duration-300 ${
                  savedRitualIds.has(ritual.id)
                    ? 'bg-gradient-to-r from-green-500 to-emerald-500 text-white cursor-default shadow-lg shadow-green-200/50'
                    : 'bg-gradient-to-r from-rose-500 via-pink-500 to-gold-500 text-white hover:shadow-2xl hover:shadow-rose-200/50 animate-pulse'
                }`}
              >
                <span className="text-xl">{savedRitualIds.has(ritual.id) ? '✓' : '📅'}</span>
                {savedRitualIds.has(ritual.id) ? 'Added to Collection' : 'Add to Wedding Plan'}
              </button>
            </div>
          )}

          {/* Explore All Button */}
          <div className="text-center mt-8">
            <Link 
              to={`/wedcraft-studio/cultural-ritual-explainer?from=modal&ref=${encodeURIComponent(themeId || themeName || '')}`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={async (e) => {
                // Track ritual navigation intent (once per theme)
                const trackKey = `ritual_nav_tracked_modal_${themeId || themeName || 'unknown'}`;
                if (!sessionStorage.getItem(trackKey)) {
                  sessionStorage.setItem(trackKey, '1');
                  try {
                    await apiService.feedback.trackRitualNavigation({
                      theme_id: themeId || null,
                      ritual_count: 1,
                      entry_point: 'modal'
                    });
                  } catch (error) {
                    console.error('Error tracking ritual navigation:', error);
                  }
                }
              }}
              className="inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-full font-bold text-lg shadow-xl hover:shadow-2xl hover:scale-105 transition-all duration-300 animate-pulse"
            >
              <span className="text-2xl">🌍</span>
              Explore All Cultural Rituals
              <span className="text-2xl">✨</span>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * Renders the AI Color Magic Modal with VowAI signature styling
 * 
 * @param {Object} colorModal - Color modal state object
 * @param {Function} setColorModal - State setter for color modal
 * @param {Function} getDisplayApplications - Function to get display applications
 * @param {Function} triggerAIMagic - Function to trigger AI magic
 * @param {Function} hexToRgb - Color conversion function
 * @param {Function} rgbToHsl - Color conversion function
 * @param {Array} categories - Category configuration array
 * @param {Component} AIMagicButton - AI Magic button component
 * @param {Component} AIColorMagicLoader - Loading component
 * @returns {JSX.Element|null} Modal JSX or null if not shown
 */
export const renderAIColorMagicModal = (
  colorModal,
  setColorModal,
  getDisplayApplications,
  triggerAIMagic,
  hexToRgb,
  rgbToHsl,
  categories,
  AIMagicButton,
  AIColorMagicLoader
) => {
  if (!colorModal.show) return null;

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-lg flex items-center justify-center z-50 p-4 animate-in fade-in duration-500">
      <div className="bg-gradient-to-br from-white via-rose-50/30 to-champagne-50/30 backdrop-blur-xl rounded-3xl shadow-2xl max-w-sm sm:max-w-md lg:max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto transform animate-in zoom-in slide-in-from-bottom-4 duration-700 border border-white/20">
        {/* VowAI Modal Header with Color Preview */}
        <div className="relative overflow-hidden">
          <div 
            className="h-32 sm:h-40 relative flex items-center justify-center"
            style={{ 
              background: `linear-gradient(135deg, ${colorModal.color}E6, ${colorModal.color}B3, ${colorModal.color}80)`
            }}
          >
            {/* Animated background pattern */}
            <div className="absolute inset-0 opacity-20">
              <div className="absolute inset-0 bg-gradient-to-br from-white/30 via-transparent to-black/20"></div>
              <div className="absolute top-4 left-4 w-24 sm:w-32 h-24 sm:h-32 bg-white/10 rounded-full animate-pulse"></div>
              <div className="absolute bottom-4 right-4 w-16 sm:w-24 h-16 sm:h-24 bg-white/10 rounded-full animate-pulse" style={{ animationDelay: '1s' }}></div>
            </div>
            
            <div className="relative z-10 text-center">
              {/* VowAI signature color circle with bounce animation */}
              <div className="relative mb-4">
                <div className="w-16 sm:w-20 h-16 sm:h-20 rounded-full border-4 border-white shadow-2xl mx-auto animate-bounce" style={{ backgroundColor: colorModal.color }}></div>
                <div className="absolute inset-0 w-16 sm:w-20 h-16 sm:h-20 rounded-full border-2 border-white/50 mx-auto animate-ping"></div>
              </div>
              
              {/* VowAI signature typography */}
              <h3 className="text-white text-xl sm:text-2xl font-serif font-bold drop-shadow-2xl mb-1 tracking-wide">
                {colorModal.colorName}
              </h3>
              <p className="text-white/90 text-xs sm:text-sm font-light tracking-wider drop-shadow-lg">
                Discovered in your dream palette
              </p>
            </div>
            
            {/* VowAI close button */}
            <button
              onClick={() => setColorModal({ show: false, color: '', position: 0, colorName: '', applications: [], selectedCategory: 'decor' })}
              className="absolute top-4 right-4 w-8 sm:w-10 h-8 sm:h-10 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center text-white transition-all duration-300 hover:scale-110 backdrop-blur-sm border border-white/20"
            >
              <span className="text-sm sm:text-lg">✕</span>
            </button>
          </div>
        </div>

        {/* VowAI Modal Content */}
        <div className="p-4 sm:p-6 lg:p-8">
          <div className="space-y-4 sm:space-y-6">
            {/* Position Info - VowAI Style with hover effect */}
            <div className="bg-gradient-to-r from-purple-50/80 via-rose-50/80 to-champagne-50/80 backdrop-blur-sm rounded-2xl p-4 sm:p-5 border border-purple-100/50 shadow-lg">
              <h4 className="font-serif font-bold text-gray-800 mb-3 flex items-center text-base sm:text-lg hover:text-transparent hover:bg-gradient-to-r hover:from-purple-600 hover:to-rose-600 hover:bg-clip-text transition-all duration-300">
                <span className="mr-3 text-xl sm:text-2xl animate-bounce">📍</span> 
                Position in Flow
              </h4>
              <p className="text-gray-700 font-light text-sm sm:text-lg">{colorModal.position.toFixed(1)}% along the gradient journey</p>
            </div>

            {/* Color Codes - VowAI Style with hover effect */}
            <div className="bg-gradient-to-r from-gray-50/80 via-white/80 to-gray-50/80 backdrop-blur-sm rounded-2xl p-4 sm:p-5 border border-gray-200/50 shadow-lg">
              <h4 className="font-serif font-bold text-gray-800 mb-4 flex items-center text-base sm:text-lg hover:text-transparent hover:bg-gradient-to-r hover:from-gray-700 hover:to-gray-800 hover:bg-clip-text transition-all duration-300">
                <span className="mr-3 text-xl sm:text-2xl animate-bounce" style={{ animationDelay: '0.2s' }}>🎨</span>
                Color Specifications
              </h4>
              <div className="space-y-2 sm:space-y-3">
                <div className="flex justify-between items-center p-2 sm:p-3 bg-white/60 rounded-xl border border-gray-100">
                  <span className="text-gray-600 font-medium text-sm sm:text-base">HEX Code:</span>
                  <code className="bg-gradient-to-r from-gray-100 to-gray-200 px-2 sm:px-3 py-1 sm:py-2 rounded-lg font-mono text-xs sm:text-sm font-bold text-gray-800 shadow-sm">
                    {colorModal.color}
                  </code>
                </div>
                {(() => {
                  const rgb = hexToRgb(colorModal.color);
                  const hsl = rgb ? rgbToHsl(rgb.r, rgb.g, rgb.b) : null;
                  return (
                    <>
                      {rgb && (
                        <div className="flex justify-between items-center p-2 sm:p-3 bg-white/60 rounded-xl border border-gray-100">
                          <span className="text-gray-600 font-medium text-sm sm:text-base">RGB Values:</span>
                          <code className="bg-gradient-to-r from-gray-100 to-gray-200 px-2 sm:px-3 py-1 sm:py-2 rounded-lg font-mono text-xs sm:text-sm font-bold text-gray-800 shadow-sm">
                            {rgb.r}, {rgb.g}, {rgb.b}
                          </code>
                        </div>
                      )}
                      {hsl && (
                        <div className="flex justify-between items-center p-2 sm:p-3 bg-white/60 rounded-xl border border-gray-100">
                          <span className="text-gray-600 font-medium text-sm sm:text-base">HSL Values:</span>
                          <code className="bg-gradient-to-r from-gray-100 to-gray-200 px-2 sm:px-3 py-1 sm:py-2 rounded-lg font-mono text-xs sm:text-sm font-bold text-gray-800 shadow-sm">
                            {hsl.h}°, {hsl.s}%, {hsl.l}%
                          </code>
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>
            </div>

            {/* Usage Suggestions - VowAI Style with hover effect and AI-powered content */}
            <div className="bg-gradient-to-r from-rose-50/80 via-pink-50/80 to-champagne-50/80 backdrop-blur-sm rounded-2xl p-4 sm:p-5 border border-rose-100/50 shadow-lg">
              <h4 className="font-serif font-bold text-gray-800 mb-4 flex items-center text-base sm:text-lg hover:text-transparent hover:bg-gradient-to-r hover:from-rose-600 hover:to-pink-600 hover:bg-clip-text transition-all duration-300">
                <span className="mr-3 text-xl sm:text-2xl animate-bounce" style={{ animationDelay: '0.4s' }}>💡</span>
                AI Color Magic
                {getDisplayApplications()[0] === 'LOADING_AI_MAGIC' && (
                  <span className="ml-2 text-xs bg-gradient-to-r from-purple-500 to-pink-500 text-white px-2 py-1 rounded-full animate-pulse">AI Powered</span>
                )}
              </h4>
              
              {/* Category Tabs - VowAI Style - Only show after AI Magic is triggered */}
              {getDisplayApplications()[0] !== 'SHOW_AI_BUTTON' && (
                <div className="flex flex-wrap gap-2 mb-4">
                {categories.map((category) => (
                  <button
                    key={category.id}
                    onClick={() => setColorModal(prev => ({ ...prev, selectedCategory: category.id }))}
                    className={`relative overflow-hidden px-3 py-2 rounded-full text-xs font-bold transition-all duration-300 flex items-center gap-1 ${
                      colorModal.selectedCategory === category.id
                        ? 'bg-gradient-to-r from-rose-500 via-pink-500 to-gold-500 text-white shadow-lg transform scale-105 animate-pulse'
                        : 'bg-white/70 text-gray-600 hover:bg-gradient-to-r hover:from-rose-100 hover:to-pink-100 hover:text-rose-600 hover:shadow-md hover:scale-105'
                    }`}
                  >
                    <span>{category.icon}</span>
                    <span>{category.name}</span>
                    {/* VowAI signature glow effect for active state */}
                    {colorModal.selectedCategory === category.id && (
                      <div className="absolute inset-0 bg-gradient-to-r from-gold-400/20 to-rose-400/20 opacity-50 rounded-full"></div>
                    )}
                  </button>
                ))}
                </div>
              )}
              
              <div className="text-gray-700 space-y-2 font-light text-sm sm:text-base">
                {getDisplayApplications()[0] === 'SHOW_AI_BUTTON' ? (
                  <AIMagicButton 
                    colorName={colorModal.colorName} 
                    onTriggerMagic={triggerAIMagic}
                  />
                ) : getDisplayApplications()[0] === 'LOADING_AI_MAGIC' ? (
                  <AIColorMagicLoader colorName={colorModal.colorName} />
                ) : getDisplayApplications()[0] === 'API_ERROR_MESSAGE' ? (
                  <div className="text-center p-6 bg-gradient-to-r from-red-50 to-orange-50 rounded-xl border-2 border-red-200">
                    <div className="text-4xl mb-3">⚠️</div>
                    <h5 className="font-bold text-red-700 mb-2">AI Service Temporarily Unavailable</h5>
                    <p className="text-red-600 text-sm mb-4">
                      Our AI color magic service is experiencing issues. Please try again in a few moments.
                    </p>
                    <button
                      onClick={triggerAIMagic}
                      className="relative overflow-hidden bg-gradient-to-r from-rose-500 via-pink-500 to-gold-500 text-white py-2 px-4 rounded-2xl font-bold hover:shadow-2xl transform hover:scale-105 transition-all duration-300 animate-pulse"
                    >
                      Try Again
                      {/* VowAI signature glow effect */}
                      <div className="absolute inset-0 bg-gradient-to-r from-gold-400/20 to-rose-400/20 opacity-0 hover:opacity-100 transition-opacity duration-300 rounded-2xl"></div>
                    </button>
                  </div>
                ) : (
                  getDisplayApplications().map((application, index) => (
                    <p key={index} className="flex items-start">
                      <span className="mr-2 text-rose-400 mt-1">•</span> 
                      <span>
                        {application}
                      </span>
                    </p>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
